/**
 * Shared Currency Utilities
 *
 * Centralized utilities for currency parsing, formatting, and rounding
 *
 * @module shared/currencyUtils
 */

/**
 * USD currency formatter using Intl API
 */
const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Round a number to two decimal places
 *
 * @param value - Value to round
 * @returns Rounded number, or 0 if not finite
 *
 * @example
 * roundToTwo(1.234) // 1.23
 * roundToTwo(1.236) // 1.24
 */
export function roundToTwo(value: unknown): number {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return 0;
  }
  return Math.round(num * 100) / 100;
}

/**
 * Format a number as USD currency
 *
 * @param amount - Amount to format
 * @returns Formatted currency string (e.g., "$1,234.56"), or null if invalid
 *
 * @example
 * formatUsd(1234.56) // "$1,234.56"
 * formatUsd(-100) // "-$100.00"
 */
export function formatUsd(amount: number | null | undefined): string | null {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    return null;
  }
  const rounded = roundToTwo(amount);
  return USD_FORMATTER.format(rounded);
}

/**
 * Parse currency value from various input formats
 * Strips currency symbols and formatting characters
 *
 * @param value - Value to parse (number, string, etc.)
 * @returns Parsed numeric value, or null if invalid
 *
 * @example
 * parseCurrency("$1,234.56") // 1234.56
 * parseCurrency("1234.56") // 1234.56
 * parseCurrency(1234) // 1234
 */
export function parseCurrency(value: unknown): number | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    // Remove currency symbols, commas, and whitespace
    const cleaned = value.replace(/[^0-9.-]+/g, '');
    if (!cleaned) {
      return null;
    }
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

/**
 * Sanitize and validate a percentage value
 *
 * @param value - Value to sanitize
 * @param fallback - Optional fallback value if invalid
 * @returns Sanitized percentage (>= 0), or fallback/undefined
 *
 * @example
 * sanitizePercentage("15.5") // 15.5
 * sanitizePercentage(-5, 0) // 0
 * sanitizePercentage("invalid", 10) // 10
 */
export function sanitizePercentage(value: unknown, fallback?: number | null): number | undefined {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isFinite(num) && num >= 0) {
    return roundToTwo(num);
  }
  return fallback ?? undefined;
}

/**
 * Sanitize and validate a currency amount
 *
 * @param value - Value to sanitize
 * @param fallback - Optional fallback value if invalid
 * @returns Sanitized currency amount (>= 0), or fallback/undefined
 *
 * @example
 * sanitizeCurrency("100.50") // 100.5
 * sanitizeCurrency(-50, 0) // 0
 */
export function sanitizeCurrency(value: unknown, fallback?: number | null): number | undefined {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isFinite(num) && num >= 0) {
    return roundToTwo(num);
  }
  return fallback ?? undefined;
}

/**
 * Format a percentage value for input fields
 *
 * @param value - Value to format
 * @returns Formatted string, or empty string if invalid
 *
 * @example
 * formatPercentageInput(15.5) // "15.5"
 * formatPercentageInput("invalid") // ""
 */
export function formatPercentageInput(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '';
  }
  return String(roundToTwo(num));
}

/**
 * Format a currency value for input fields
 *
 * @param value - Value to format
 * @returns Formatted string with two decimal places
 *
 * @example
 * formatCurrencyInput(100.5) // "100.50"
 * formatCurrencyInput("invalid") // "0.00"
 */
export function formatCurrencyInput(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '0.00';
  }
  return roundToTwo(num).toFixed(2);
}
